import clsx from 'clsx';
import { Link, useParams } from 'react-router';

import { routes } from '@/assets/constants/routes-no.ts';
import { Breadcrumbs } from '@/components/Breadcrumbs/index.ts';
import { useStartRefinFlow } from '@/hooks/useStartRefinFlow.ts';
import { sendUserPortalEvent } from '@/lib/analytics/unified.ts';
import { Button } from '@/ui/button-2.tsx';

import { ArticleCard } from '../-components/article-card.tsx';
import { getAllTags, getArticles } from '../registry.ts';
import { ARTICLES_INDEX_PATH, formatArticleTagLabel, getArticleTagPath } from '../tags.ts';

const filterChipClassName = (active: boolean) =>
  clsx(
    'rounded-2xl px-3 py-1 typography-v1-body4 text-v1-background-200 no-underline',
    active
      ? 'border border-v1-neutral-40 bg-v1-neutral-20'
      : 'border border-v1-neutral-20 bg-v1-neutral-20 hover:border-v1-neutral-40',
  );

export default function ArticlesIndexPage() {
  const { tag } = useParams<{ tag?: string }>();
  const startRefinFlow = useStartRefinFlow();
  const tags = getAllTags();
  const activeTag = tag && tags.includes(tag) ? tag : undefined;
  const articles = getArticles(activeTag ? { tag: activeTag } : undefined);

  return (
    <div className="w-full bg-white py-4 pb-16 sm:py-8 xl:py-16">
      <title>Artikler</title>

      <article className="mx-auto flex max-w-6xl flex-col gap-8 px-4">
        <Breadcrumbs
          className="max-md:-mb-4"
          items={[
            { text: 'Hjem', url: '/' },
            { text: 'Din økonomi', url: routes.MY_ECONOMY },
            { text: 'Artikler' },
          ]}
        />
        <h1 className="typography-v1-display">Artikler</h1>

        <div className="flex flex-col gap-2">
          <p className="typography-v1-body3 text-v1-background-200">Sorter:</p>
          <div className="flex flex-wrap gap-2">
            <Link
              to={ARTICLES_INDEX_PATH}
              className={filterChipClassName(!activeTag)}
              onClick={() => {
                sendUserPortalEvent('articles_filter_clicked', { tag: 'all' });
              }}
            >
              Alle artikler
            </Link>
            {tags.map((tag) => (
              <Link
                key={tag}
                to={getArticleTagPath(tag)}
                className={filterChipClassName(activeTag === tag)}
                onClick={() => {
                  sendUserPortalEvent('articles_filter_clicked', { tag });
                }}
              >
                {formatArticleTagLabel(tag)}
              </Link>
            ))}
          </div>
        </div>

        <ul className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {articles.map((article) => (
            <li key={article.slug}>
              <ArticleCard meta={article} source="articles_index" />
            </li>
          ))}
        </ul>

        <div className="flex flex-col items-center gap-6">
          <p className="mx-auto mt-16 max-w-[700px] text-center typography-v1-h3 sm:mt-32">
            Se om du kan få en bedre deal på forbrukslån eller refinansiering.
          </p>
          <Button
            color="yellow"
            onClick={() => {
              sendUserPortalEvent('articles_index_cta_clicked');
              startRefinFlow();
            }}
          >
            Sjekk hvor mye du kan spare
          </Button>
        </div>
      </article>
    </div>
  );
}
